import { Select, Input, Button, Spin } from "antd";
import React, { useState, useEffect, useCallback } from "react";
import CalendarViewComponent from "../../../components/CalendarView/CalendarViewComponent";
import { httpService } from "../../../../services/httpService.service";
import PreviewModal from "../modals/PreviewModal";
import { debounce } from "lodash";
import { months, years } from "../../../../services/Enums/monthsAndYears";
import { ipStatus } from "../../../../services/Enums/ipPlanEnums";

const { Option } = Select;

const CalendarViewPlanning = () => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [clickedEventId, setClickedEventId] = useState();
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [ipPlans, setIPPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    ImplementingPartnerId: undefined,
    month: undefined,
    year: undefined,
    IPStatus: undefined,
    AssignedTo: undefined,
    search: "",
  });

  const [inputSearch, setInputSearch] = useState(filters.search);
  const [implementingPartners, setImplementingPartners] = useState([]);

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleFilterChange = (type, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [type]: value }));
  };

  const debouncedSearchHandler = useCallback(
    debounce((query) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        search: query,
      }));
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    const search = e.target.value;
    setInputSearch(search);
    debouncedSearchHandler(search);
  };

  const handleClearFilters = () => {
    setFilters({
      ImplementingPartnerId: undefined,
      month: undefined,
      year: undefined,
      IPStatus: undefined,
      AssignedTo: undefined,
      search: "",
    });
    setInputSearch("");
  };

  useEffect(() => {
    const fetchImplementingPartners = async () => {
      const workspaceId = localStorage.getItem("currentContractId");
      const apiIps = `/api/ImplementingPartner/getAllImplementingPartners?contractId=${workspaceId}&IgnorePagination=true`;

      httpService.get(
        apiIps,
        (res) => setImplementingPartners(res.data),
        (error) => console.error("Error fetching Implementing Partners:", error)
      );
    };
    fetchImplementingPartners();

    const workspaceId = localStorage.getItem("currentContractId");
    httpService.get(
      `/api/User/getAllusers?RoleIds=e78550f4-c017-4ec7-94e8-e040090c32b1&RoleIds=615a8dc6-ca07-4f82-8d0d-7d82dd4d3ef8&pageSize=1000&ContractIds=${workspaceId}&IgnorePagination=true`,
      (res) => setUsers(res.data)
    );
  }, []);

  const fetchIPPlans = async () => {
    const projectId = localStorage.getItem("currentProjectID");
    const filtersData = {
      ProjectId: projectId,
      ImplementingPartnerId: filters.ImplementingPartnerId,
      Year: filters.year,
      Month: filters.month,
      IPStatus: filters.IPStatus,
      AssignedTo: filters.AssignedTo,
      Search: filters.search,
      IgnorePagination: true,
    };

    // Filter out undefined values
    const params = Object.fromEntries(
      Object.entries(filtersData).filter(([_, v]) => v !== undefined)
    );

    const queryString = new URLSearchParams(params).toString();
    const url = `/api/ipplan/getAllIPPlans?${queryString}`;

    setLoading(true);

    httpService.get(
      url,
      (response) => {
        const ipPlansData = response.data?.map((ipPlan) => ({
          date: ipPlan?.initialMeeting,
          identifier: ipPlan?.identifier,
          id: ipPlan?.id,
        }));
        setIPPlans(ipPlansData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching IP Plans:", error);
        setLoading(false);
      }
    );
  };

  const handleEventClicked = (eventId) => {
    setClickedEventId(eventId);
    if (eventId) {
      setShowPreviewModal(!showPreviewModal);
    }
  };

  const handlePreviewOk = () => {
    setShowPreviewModal(false);
  };

  const handleCancel = () => {
    setShowPreviewModal(false);
  };

  useEffect(() => {
    fetchIPPlans();
  }, [filters]);

  const events = ipPlans.reduce((acc, ipPlan) => {
    if (!ipPlan) return acc;
    const dateKey = ipPlan.date?.split("T")[0];

    if (dateKey) {
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push({
        type: "success",
        status: "test",
        content: ipPlan.identifier,
        id: ipPlan.id,
      });
    }

    return acc;
  }, {});

  const getListData = (value) => {
    const dateKey = value.format("YYYY-MM-DD");
    const filteredEvents = events[dateKey] || [];
    return filteredEvents.filter((event) => {
      let passFilters = true;
      if (filters.status && event.status !== filters.status) {
        passFilters = false;
      }
      if (filters.startDate && filters.endDate) {
        const eventDate = new Date(dateKey);
        if (
          eventDate < new Date(filters?.startDate) ||
          eventDate > new Date(filters?.endDate)
        ) {
          passFilters = false;
        }
      }
      if (
        filters.search &&
        !event.content.toLowerCase().includes(filters.search.toLowerCase())
      ) {
        passFilters = false;
      }
      return passFilters;
    });
  };

  return (
    <>
      <div className="filtersWrapper my-5">
        <div className="containerFilters">
          <Input
            placeholder="Type something here"
            name="search"
            value={inputSearch}
            onChange={handleSearchChange}
          />
          <Button className="clearButton" onClick={handleClearFilters}>
            Clear All
          </Button>
          <Button onClick={handleToggleFilters} className="toggleButton">
            {!showFilters ? "Show Filters" : "Hide Filters"}
          </Button>
        </div>
        {showFilters && (
          <div className="filtersContainer">
            <div className="filter">
              <p className="filterText">Implementing Partner</p>
              <Select
                style={{ width: 200, marginBottom: 20 }}
                placeholder="Select an IP"
                onChange={(value) =>
                  handleFilterChange("ImplementingPartnerId", value)
                }
                value={filters.ImplementingPartnerId}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().startsWith(input.toLowerCase())
                }
              >
                {implementingPartners.map((implementingPartner) => (
                  <Option
                    key={implementingPartner.id}
                    value={implementingPartner.id}
                  >
                    {implementingPartner.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="filter">
              <p className="filterText">Current IP Status</p>
              <Select
                style={{ width: 200, marginBottom: 20 }}
                placeholder="Select an IP status"
                onChange={(value) => handleFilterChange("IPStatus", value)}
                value={filters.IPStatus}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().startsWith(input.toLowerCase())
                }
              >
                {Object.entries(ipStatus)
                  .filter(([key]) => key !== "default")
                  .map(([key, value]) => (
                    <Option key={key} value={key}>
                      {value.text}
                    </Option>
                  ))}
              </Select>
            </div>
            <div className="filter">
              <p className="filterText">Assigned To:</p>
              <Select
                style={{ width: 200, marginBottom: 20 }}
                placeholder="Select a user"
                onChange={(value) => handleFilterChange("AssignedTo", value)}
                value={filters.AssignedTo}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().startsWith(input.toLowerCase())
                }
              >
                {users &&
                  users.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.userDetails.firstName
                        ? user.userDetails.firstName
                        : "N/A"}{" "}
                      {user.userDetails.lastName
                        ? user.userDetails.lastName
                        : "N/A"}{" "}
                      - ({user.userName})
                    </Option>
                  ))}
              </Select>
            </div>
            <div className="filter">
              <p className="filterText">Month</p>
              <Select
                style={{ width: 200, marginBottom: 20 }}
                placeholder="Select a month"
                onChange={(value) => handleFilterChange("month", value)}
                value={filters.month}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().startsWith(input.toLowerCase())
                }
              >
                {Object.entries(months).map(([key, value]) => (
                  <Option key={key} value={key}>
                    {value}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="filter">
              <p className="filterText">Year</p>
              <Select
                style={{ width: 200, marginBottom: 20 }}
                placeholder="Select a year"
                onChange={(value) => handleFilterChange("year", value)}
                value={filters.year}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const children = React.Children.toArray(option.children).join(
                    ""
                  );
                  return children.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {years.map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </div>

      <Spin spinning={loading}>
        <CalendarViewComponent
          getListData={getListData}
          setClickedEventId={handleEventClicked}
        />
      </Spin>
      <PreviewModal
        visible={showPreviewModal}
        record={clickedEventId}
        onOk={handlePreviewOk}
        onCancel={handleCancel}
      />
    </>
  );
};

export default CalendarViewPlanning;
